import {
  ACTIONS_PER_TURN,
  REACTION_SLOT_ALLOWANCE,
  type Combatant,
  type EncounterState,
  type TurnBudget,
} from "@lancer-vtt/domain";
import { previewIfControlled } from "../activationPreview";

/**
 * The **ONE pre-activation economy read** the three bespoke inspector bodies
 * ({@link MechInspectorBody}, {@link PilotInspectorBody}, {@link NpcInspectorBody}) and the two
 * budget-gated choosers ({@link BarrageChooser}, {@link FullTechChooser}) share —
 * `projects/pre-activation-affordances.md` items 2.2/2.3, over decision **D4**.
 *
 * ## What it is for
 *
 * The room now folds a real `activate_unit` in front of a controlled, `"ready"` unit's first
 * action (D1/D2, `backend/src/encounterActivationFold.ts`), and it runs the whole gate ladder +
 * referee pass against {@link previewActivation}'s hypothetical post-activation state
 * (`encounterRoomCore.ts`'s `resolutionState`). So the ability bar must read that SAME derivation,
 * or the board keeps saying `IDLE` at a unit whose actions the server will happily resolve — the
 * "correctly-derived field no render site read" failure D8 row 4 names by name.
 *
 * **One concept, used twice.** This composes the *existing, untouched* economy reads against the
 * preview; it does not loosen a single legality function (D4 forbids that, and `moveBudgetFor` /
 * `reachableForCombatant` are read by the `move_token` reducer and the bot respectively). The
 * preview object itself is read-only: never committed, never dispatched, never handed to a reducer
 * (see {@link previewActivation}'s never-commit invariant).
 *
 * ## The `controls` half, and why `canControl` is the wrong predicate
 *
 * The fold's first arm — `perUnitState === "ready"` ∧ *strict slot identity*, never the frontend's
 * GM-inclusive `canControl` prop — lives in {@link previewIfControlled} (`../activationPreview`),
 * which this and every other pre-activation affordance in the browser now share. **Read that
 * function's doc for the full argument**; it is not restated here.
 *
 * The practical consequence, stated because it looks like a bug and is not: today's NPCs freeze
 * `controllerSlotId: null` (`spawnNpc`), so a GM-owned NPC is never `controls`-matched and its
 * bar keeps reading `IDLE` before activation. That is exactly right — the server will wave that
 * GM's intent through **uncosted**, with no activation recorded, per D2's documented adjudication
 * escape. A player-controlled mech (or an NPC assigned to a slot via `set_controller`) is the case
 * that goes live.
 *
 * This is the same trap the tracker's Gotchas record from the Phase 1 arm-order correction: a
 * confident claim about what "a GM controls" that is false for `controlsCombatant`. Verified here
 * against `capabilities.ts`, not inherited from a comment.
 */
export type InspectorEconomy = {
  /**
   * The state every economy/legality read in the bar and the choosers composes against — the real
   * `state`, or {@link previewActivation}'s hypothetical post-activation state when the fold would
   * happen. **Read-only** (see the module header's never-commit note).
   */
  state: EncounterState;
  /** Is this unit genuinely mid-turn *right now*? The literal `turn.phase === "acting" && turn.actor === c.id` this replaced. Still the correct read for END TURN / the Advisor / autoplay — anything about the turn that is actually open, rather than about what an action would open. */
  isActiveActor: boolean;
  /** The bar reads live: the unit is mid-turn, **or** its first action would fold an activation. */
  economyLive: boolean;
  /** The pre-activation half alone — `economyLive && !isActiveActor`. The one flag a caller needs to say "this readout is a *preview* of a turn that has not opened yet". */
  previewing: boolean;
  /** The acting `TurnBudget` — real or previewed — or `undefined` when the bar is not live. Feeds `enumerateActions`'s `budget` (the once-per-turn / scene-cap `usedUp` read). */
  budget: TurnBudget | undefined;
  /** Action points to render, falling back to the full allotment when the bar is not live (the pre-existing off-turn placeholder). */
  ap: number;
  /** The unit's remaining reaction slot, read off {@link state} (the preview refreshes it exactly as `activate()` does). */
  reactionsLeft: number;
  quickOpen: boolean;
  fullOpen: boolean;
  reactOpen: boolean;
  quickAvail: string;
  fullAvail: string;
  reactAvail: string;
};

/**
 * Derive the {@link InspectorEconomy} for `combatant` as seen by the slot `actorId`. Pure, total,
 * and cheap — safe to call once per inspector render.
 *
 * **Exported and unit-tested directly, not through a DOM render**: this repo has no jsdom/headless
 * React renderer (see `combatWindowsChoosers.test.ts`'s header for the convention), so the
 * "component tier" D8 row 4 asks for is this function driven against a constructed
 * `EncounterState` — the exact function the three bodies call, not a re-derivation.
 */
export function inspectorEconomy(args: {
  state: EncounterState;
  combatant: Combatant;
  /** The viewer's own slot id — the same identity the room attributes an intent to. */
  actorId: string | null | undefined;
  /** The viewer is the GM, who folds on GM-owned units (`FoldViewer`). */
  isGm: boolean;
}): InspectorEconomy {
  const { state, combatant: c, actorId, isGm } = args;
  const realTurn = state.activation.turn;
  const isActiveActor = realTurn.phase === "acting" && realTurn.actor === c.id;
  // `activationDisposition`'s first arm, client-side — see {@link previewIfControlled}. Extracted
  // to its own module in 2.4 so the movement wash, the drag affordance and `dispatchMove`'s local
  // dry-run compose the SAME expression this bar does rather than three hand-kept copies of it.
  const previewed = previewIfControlled(state, c, { actorId, isGm });
  const turn = previewed.activation.turn;
  const economyLive = turn.phase === "acting" && turn.actor === c.id;
  const budget: TurnBudget | undefined = turn.phase === "acting" && turn.actor === c.id ? turn.budget : undefined;
  const ap = budget?.actions ?? ACTIONS_PER_TURN;
  const reactionsLeft = previewed.activation.reactionSlot[c.id] ?? REACTION_SLOT_ALLOWANCE;
  return {
    state: previewed,
    isActiveActor,
    economyLive,
    previewing: economyLive && !isActiveActor,
    budget,
    ap,
    reactionsLeft,
    quickOpen: economyLive && ap > 0,
    fullOpen: economyLive && ap >= ACTIONS_PER_TURN,
    reactOpen: economyLive && reactionsLeft > 0,
    quickAvail: economyLive ? (ap > 0 ? `${ap} LEFT` : "NONE") : "IDLE",
    fullAvail: economyLive ? (ap >= ACTIONS_PER_TURN ? "OPEN" : "SPENT") : "IDLE",
    reactAvail: economyLive ? (reactionsLeft > 0 ? "OPEN" : "SPENT") : "IDLE",
  };
}

/**
 * **The Boost row's honest "why not", and the one row on the bar that needs one.**
 *
 * Every other live row reaches the wire as an `encounter_intent`, and
 * `RemoteEncounterController.sendIntent` runs no local legality dry-run — so an ungreyed row
 * genuinely reaches the room, which folds an activation for it when the caller controls a `"ready"`
 * unit (D1/D2). **Boost is the exception**: it rides in as an `encounter_command` through
 * `RemoteController.dispatch`, whose documented contract is *"a local reducer rejection is surfaced
 * immediately and NOT sent"*, and the domain reducer refuses it outright unless the unit is the one
 * mid-turn (`packages/domain/src/encounter/handlers/budget.ts` — `boost: no active actor`, then
 * `is not the active actor`). The three bodies drop that `DispatchResult` on the floor, so any row
 * the wire cannot carry is a button that does **nothing at all, silently**.
 *
 * ## What 3.1 changed, and why this did not simply go away
 *
 * 3.1 gave Boost the client-side lead the fold gives every intent: a controlled, `"ready"` unit's
 * Boost dispatches `activate_unit` then `boost` as two ordinary commands
 * ({@link withLeadingActivation}), so **that** case is now live rather than greyed — which is the
 * whole point of the item, and the reason this function's condition moved from `!isActiveActor` to
 * `!economyLive`.
 *
 * What it did **not** change is every other off-turn case: an off-side unit, one that has already
 * acted, a destroyed one, one belonging to another player, or a ready one while somebody else is
 * mid-turn. None of those can be activated, so their gesture is a bare `boost` the local reducer
 * refuses and the controller never sends. `economyLive` is exactly "the gesture will apply locally
 * and be sent", so its negation is exactly the dead-click set. Greying those **with a reason** is
 * the pre-existing silent-dead-click 2.2 closed and 3.1 must not reopen — the column's own dimming
 * cannot do it, because `is-unavail` is opacity, not `pointer-events`
 * (`frontend/src/routes/home/combat-windows.css`), so the row stays clickable.
 */
export function boostBlockReason(economy: InspectorEconomy, rowId: string): string | null {
  if (rowId !== "boost" || economy.economyLive) return null;
  return "this unit can't Boost right now — its turn isn't open and it can't take one yet";
}

/**
 * The **budget-gated choosers' shared economy read** — {@link BarrageChooser} and
 * {@link FullTechChooser} (2.3). Both open with the identical three lines, and both are handed
 * {@link InspectorEconomy}`.state` by their host body, so a `"ready"` unit's chooser reads the
 * previewed turn the fold will open rather than the idle one it still carries. Before 2.3 both
 * rendered a hard *"ACTIVATE this mech's turn to Barrage."* empty state pre-activation, which is
 * the wall this lifts.
 *
 * **Exported for testability, not reuse beyond those two** — same reason as
 * {@link classifySkirmishChooser}/{@link classifyBarrageSlot}: no jsdom renderer, so the test drives
 * the exact function the component calls instead of re-deriving its expression.
 *
 * `turnOpen` is deliberately NOT named `isActiveActor` any more: under the preview contract it
 * means *"this unit's turn is the one on the state I was handed"*, which is true both mid-turn and
 * pre-activation.
 */
export function chooserEconomy(
  state: EncounterState,
  combatantId: string,
): { turnOpen: boolean; budgetActions: number; fullAvailable: boolean } {
  const turn = state.activation.turn;
  const turnOpen = turn.phase === "acting" && turn.actor === combatantId;
  const budgetActions = turn.phase === "acting" ? turn.budget.actions : 0;
  return { turnOpen, budgetActions, fullAvailable: turnOpen && budgetActions >= ACTIONS_PER_TURN };
}

/**
 * **The generic body's affordance mirror** — the reason {@link InspectorActionBar}'s Skirmish /
 * Activate buttons must be greyed for this viewer, or `null` when they are legitimately live
 * (`projects/pre-activation-affordances.md` 2.5).
 *
 * ## What this closes
 *
 * The three *bespoke* bodies (Mech / Pilot / Npc) route every action row through
 * {@link inspectorEconomy}, so their bars already say what the room will do. The **generic**
 * `InspectorBody` — the body for `kind` ∈ object / deployable / drone / mine — did not: it rendered
 * the action bar behind the GM-inclusive `canControl` **alone**, with Skirmish disabling only on an
 * empty candidate list and Activate not disabling at all. A drone's `controllerSlotId` derives from
 * its deployer, so a **player** can own one and press those buttons.
 *
 * ## What the room actually does, which is what this mirrors
 *
 * `activationDisposition`'s arms, read top-down: `ready ∧ controls` → activate; GM → wave through;
 * otherwise → **reject**. None of these kinds is an {@link isActor}, so `perUnitState` gives them no
 * entry at all and {@link previewActivation} hands back `state` unchanged — a drone is never
 * `"ready"`, so arm 1 can never fire for one. A player-attributed press therefore meets the reject
 * arm; a GM-attributed press takes the documented wave-through escape and resolves (uncosted, on a
 * still-idle turn) exactly as it does today.
 *
 * So: `isGm` is the live case, and this returns a reason for everyone else. **`economyLive` is in
 * the condition deliberately even though it is unreachable for today's kinds** — it is the shared
 * vocabulary the bespoke bodies use, and it is what keeps this honest if a kind ever becomes an
 * actor or a future drone-attack project reopens the reject arm on purpose (the tracker's Risks
 * record that it will have to).
 *
 * **This is a latent gap, not a live bug**: no shipped drone carries a weapon and the drone surface
 * is deploy/detect/disarm only, so nothing could actually fire through those buttons today. It is
 * closed because the client should not offer a button the server will refuse — the same reason
 * {@link boostBlockReason} exists for Boost.
 */
export function genericActionBarBlockReason(args: {
  state: EncounterState;
  combatant: Combatant;
  /** The viewer's own slot id — the same identity the room attributes an intent to. */
  actorId: string | null | undefined;
  /** Is this client the GM? The room's wave-through arm, which is a role read and NOT `controlsCombatant`. */
  isGm: boolean;
}): string | null {
  const { state, combatant, actorId, isGm } = args;
  if (isGm) return null;
  if (inspectorEconomy({ state, combatant, actorId, isGm }).economyLive) return null;
  return "this unit never takes an activation of its own, so the room refuses an action from it — the GM can resolve one";
}
